/* eslint-disable no-param-reassign */
import _ from 'underscore';
import {
  BulkUploadSchema, AdvertAttributes, CsvRow, TraverseResult, ErrorRow,
} from '../types';

// this is for backward compatibility. legacy code put schema to property named schema
function getSchemaOfSubAttr(schema: BulkUploadSchema, attrName: string): BulkUploadSchema {
  if (schema.properties[attrName].type === 'array') {
    return (schema.properties[attrName].items as { schema?: BulkUploadSchema }).schema || (schema.properties[attrName].items) as BulkUploadSchema;
  }
  return schema.properties[attrName].schema || schema.properties[attrName] as unknown as BulkUploadSchema;
}

// this is for backward compatibility. new schema put name in $$ODataExtension
function getSchemaName(schema: BulkUploadSchema): string | undefined {
  return schema.name || (schema.$$ODataExtension && schema.$$ODataExtension.Name);
}

export function serialize(schema: BulkUploadSchema, attrs: AdvertAttributes | AdvertAttributes[]): string[] {
  const rows = getCSVRows(schema, attrs);
  const formatRow = {
    Type: 'Format Version',
    Name: '6',
  };
  rows.splice(0, 0, formatRow);
  return generateCsvRows(rows);
}

function getCSVRows(rootSchema: BulkUploadSchema, attributesList: AdvertAttributes | AdvertAttributes[], clientIdPrefix?: string): CsvRow[] {
  const csvRows: CsvRow[] = [];

  // ensuring that the input list is an array (first level entity is always a solo object)
  if (!_.isArray(attributesList)) {
    attributesList = [attributesList];
  }

  const traverses: TraverseResult[] = [];
  const clientIds: string[] = [];
  _.each(attributesList, (attributes, index) => {
    if (
      (!_.isString(attributes['Client Id']) && !_.isNumber(attributes['Client Id']))
      || _.contains(clientIds, attributes['Client Id'])
    ) {
      attributes['Client Id'] = (clientIdPrefix ? (`${clientIdPrefix}-`) : '') + index.toString();
    }
    const traverseResult = traverseFields(attributes, rootSchema);
    traverses.push(traverseResult);
    clientIds.push(attributes['Client Id']);
  });

  const nextRootResults: CsvRow[][] = [];
  _.each(traverses, (rowResult, rowIndex) => {
    const { attributes } = rowResult;
    const { currentRow } = rowResult;
    const { nextRoots } = rowResult;
    const { nextRootSchemas } = rowResult;
    if (rootSchema.properties['bulk-upload-type']) {
      currentRow.Type = rootSchema.properties['bulk-upload-type'].value;
    }
    csvRows.push(currentRow);
    _.each(nextRoots, (nextRoot, index) => {
      const nestedCsvRows = getCSVRows(nextRootSchemas[index], attributes[nextRoot], `${clientIds[rowIndex]}-${index.toString()}`);
      nextRootResults.push(nestedCsvRows);
    });
  });

  _.each(_.flatten(nextRootResults, true), (row) => {
    csvRows.push(row);
  });

  return csvRows;
}

function getCsvHeaderName(attr: string, schema: BulkUploadSchema): string | undefined {
  return !_.isUndefined(schema.properties[attr]['bulk-upload-header'])
    ? schema.properties[attr]['bulk-upload-header']
    : attr;
}

function getCsvValue(attr: string, schema: BulkUploadSchema, value: any): string {
  if (
    _.isNull(value)
    || (schema.properties[attr].bulkDeleteValueIfEmpty
      && ((_.isString(value) && _.isEmpty(value.trim())) || _.isEmpty(value)))
  ) {
    return 'delete_value';
  }

  const func = schema.properties[attr]['bulk-upload-mapper'];
  value = !_.isFunction(func) ? value : func(value);

  if (_.contains(value, '"')) {
    value = value.replace(/"/g, '""');
  }

  if (_.contains(value, ',') || _.contains(value, '"') || _.contains(value, '\n')) {
    value = `"${value}"`;
  }

  return value;
}

// traversing fields for each set of attributes in the list, returning:
// - currentRow: data to send to MT for the given object(csv header names and values)
// - nextRoots: inner objects that need to be traversed again for new data (like when Campaign includes AdGroups)
// - nextRootSchemas: Schemas to be used for the next inner objects (based on their type).
// - attributes: attributes that are passed to the function with the client Ids added to them.
function traverseFields(attrs: AdvertAttributes, schema: BulkUploadSchema): TraverseResult {
  const currentRow: CsvRow = {};
  const nextRoots: string[] = [];
  const nextRootSchemas: BulkUploadSchema[] = [];

  _.each(attrs, (value, attr) => {
    if (attr === 'Client Id') {
      currentRow[attr] = value;
      return;
    }
    // reject the function if there is any attribute not found in the schema
    if (!_.has(schema.properties, attr)) {
      throw new Error(`invalid property ${attr} is passed!`);
    }
    let innerPropertySchema: BulkUploadSchema | null = null;
    // deciding if the current attr has schema
    if (schema.properties[attr].type === 'object' || schema.properties[attr].type === 'array') {
      innerPropertySchema = getSchemaOfSubAttr(schema, attr);
    }
    if (innerPropertySchema) {
      // skipping the budget schema as it has the Id but it shouldn't be considered as a root entity
      // todo [imang]: this should be a temporary fix. We have to think this through and find the optimal solution to find root entities
      // todo: SharedEntityAssociation is root, however it has EntityId and SharedEntityId, but not Id
      const isRootEntity = getSchemaName(innerPropertySchema) === 'SharedEntityAssociation'
        || (getSchemaName(innerPropertySchema) !== 'Budget'
          && getSchemaName(innerPropertySchema) !== 'AssetLink'
          && _.has(innerPropertySchema.properties, 'Id'));
      // deciding if the attr is a foreign key link or a complete object needs to be further traversed
      const attrKeys = _.keys(value);
      const isObjectAForeignKey = schema.properties[attr].type === 'object' && _.size(attrKeys) === 1 && attrKeys[0] === 'Id';
      if (isRootEntity) {
        if (isObjectAForeignKey) {
          // todo [imang]: currently the only relationship defined and handled in the adapter is the Parent Relationship. Should make this more generic in the future.
          currentRow['Parent ID'] = value.Id;
        } else {
          nextRoots.push(attr);
          nextRootSchemas.push(innerPropertySchema);
        }
      } else if (schema.properties[attr]['bulk-upload-mapper']) {
        // check if it needs custom mapping
        currentRow[getCsvHeaderName(attr, schema)!] = getCsvValue(attr, schema, value);
      } else if ((getSchemaOfSubAttr(schema, attr) as any)['bulk-upload-full-mapper']) {
        if (_.isFunction((getSchemaOfSubAttr(schema, attr) as any)['bulk-upload-full-mapper'])) {
          const keyValues = (getSchemaOfSubAttr(schema, attr) as any)['bulk-upload-full-mapper'](value);
          _.each(keyValues, (pair: { key: string; value: string }) => {
            currentRow[pair.key] = pair.value;
          });
        }
      } else {
        // if the loaded schema is not representing a root entity
        // extract the values from inside and populate to the current row object (e.g. Budget)
        // TODO [imang]: currently only supports one level traversal
        // TODO [imang]: handling special case for the Budget entity that has other properties that should be skipped.
        const propsToIterate = getSchemaName(innerPropertySchema) === 'Budget'
          ? _.pick(innerPropertySchema.properties, 'Amount', 'Type')
          : innerPropertySchema.properties;
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const prop in propsToIterate) {
          currentRow[getCsvHeaderName(prop, innerPropertySchema)!] = getCsvValue(prop, innerPropertySchema, value[prop]);
        }
      }
    } else if (schema.properties[attr]['bulk-upload-flattened']) {
      if (schema.properties[attr]['bulk-upload-enum']) {
        const enumIndex = schema.properties[attr].enum!.indexOf(value);
        const csvHeaderName = schema.properties[attr]['bulk-upload-enum']![enumIndex];
        currentRow[csvHeaderName] = 'ON';
      }
    } else if (_.isFunction(schema.properties[attr]['bulk-upload-full-mapper'])) {
      const pairs = schema.properties[attr]['bulk-upload-full-mapper']!(value);
      _.each(pairs, (pair: { key: string; value: string }) => {
        currentRow[pair.key] = pair.value;
      });
    } else {
      currentRow[getCsvHeaderName(attr, schema)!] = getCsvValue(attr, schema, value);
    }
  });

  return {
    attributes: attrs,
    currentRow,
    nextRoots,
    nextRootSchemas,
  };
}

function getCsvColumns(rows: CsvRow[]): string[] {
  return _.uniq(_.union(_.flatten(_.map(rows, (row) => _.keys(row))), ['Id', 'Parent ID']));
}

function generateCsvRows(rows: CsvRow[]): string[] {
  const columns = getCsvColumns(rows);
  const result: string[] = [];
  // first line is the list of columns
  let line = columns.join();
  result.push(line);

  _.each(rows, (row) => {
    line = '';
    _.each(columns, (column) => {
      line += `${(_.has(row, column) && !_.isUndefined(row[column])) ? row[column] : ''},`;
    });
    line = line.slice(0, -1);
    result.push(line);
  });
  return result;
}

function convertCsvDataInTextToCsvArray(csvData: string): string[][] {
  const csvDataArray: string[] = [];
  const csvDataRows: string[][] = [];
  let pos = 0;

  while (pos < csvData.length - 1) {
    csvDataArray.push(csvData.slice(pos, csvData.indexOf('\n', pos) - 1));
    pos = csvData.indexOf('\n', pos) + 1;
  }

  _.each(csvDataArray, (csvDataRow) => {
    const rowData = csvDataRow.split(',');
    csvDataRows.push(rowData);
  });

  return csvDataRows;
}

export function parse(csvData: string | string[][], isTextType?: boolean): { results: AdvertAttributes[]; errors: ErrorRow[] } {
  const results: AdvertAttributes[] = [];

  if (!_.isUndefined(isTextType)) {
    csvData = convertCsvDataInTextToCsvArray(csvData as string);
  }

  const headers = (csvData as string[][])[0];
  // first line is header, second line is format version. needs to be skipped.
  const data = (csvData as string[][]).slice(2);
  _.each(data, (row) => {
    const obj: AdvertAttributes = {};
    for (let col = 0; col < (row as string[]).length; col += 1) {
      obj[headers[col]] = (row as string[])[col];
    }
    results.push(obj);
  });

  const errors = getErrorRows(results);
  return {
    results,
    errors,
  };
}

export function findEntityWithClientId(data: AdvertAttributes | AdvertAttributes[], clientId: string): AdvertAttributes | null {
  if (!_.isArray(data)) {
    if (data['Client Id'] === clientId) {
      return data;
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const attr in data) {
      if (_.isArray(data[attr])) {
        return findEntityWithClientId(data[attr], clientId);
      }
    }
  } else {
    let ret: AdvertAttributes | null = null;
    // eslint-disable-next-line consistent-return
    _.each(data, (singleData) => {
      ret = findEntityWithClientId(singleData, clientId);
      if (ret) {
        return ret;
      }
    });
    if (ret) {
      return ret;
    }
  }
  return null;
}

function setId(data: AdvertAttributes | AdvertAttributes[], clientId: string, id: string): void {
  if (!_.isArray(data)) {
    if (data['Client Id'] === clientId) {
      data.Id = id;
      // delete data['Client Id'];
    } else {
      // eslint-disable-next-line no-restricted-syntax
      for (const attr in data) {
        if (_.isArray(data[attr])) {
          setId(data[attr], clientId, id);
        }
      }
    }
  } else {
    _.each(data, (singleData) => {
      setId(singleData, clientId, id);
    });
  }
}

export function populateIds(csvData: CsvRow[], clientData: AdvertAttributes | AdvertAttributes[]): AdvertAttributes | AdvertAttributes[] {
  const ret = clientData;
  _.each(csvData, (csvRow) => {
    if ((csvRow).Id) {
      setId(ret, csvRow['Client Id'], csvRow.Id);
    }
  });
  return ret;
}

function getErrorRows(csvData: CsvRow[]): ErrorRow[] {
  const errors = _.filter(csvData, (item) => item.Type.indexOf(' Error') > -1);

  // Parsing error process-> Property-> EditorialCode->ErrorNumber->Error
  return _.map(errors || [], (error): ErrorRow => ({
    EditorialCode: error['Editorial Reason Code'] || '',
    EditorialLocation: error['Editorial Location'] || '',
    EditorialTerm: error['Editorial Term'] || '',
    ErrorNumber: error['Error Number'] || '',
    Property: error['Field Path'] || '',
    Error: (error).Error || '',
    'Client Id': error['Client Id'] || '',
    Type: (error).Type || '',
    Id: (error).Id || '',
    'Parent Id': error['Parent Id'] || '',
  }));
}
